/**
 * 📦 CabinetAnnouncements.jsx - TAM PEŞƏKAR VERSİYA
 * 
 * Fayl yeri: src/components/cabinet/CabinetAnnouncements.jsx
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { HerracContext } from '../../App';
import useInfiniteScroll from '../useInfiniteScroll';
import Announcement from '../announcements/Announcement';
import {
  FaPlus,
  FaBox,
  FaFilter,
  FaSpinner,
  FaRedo,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChartBar
} from 'react-icons/fa';

const PAGE_SIZE = 12;
const API_URL = 'https://api.herracim.az/api';

function CabinetAnnouncements() {
  const navigate = useNavigate();
  const { user } = useContext(HerracContext);

  const [announcements, setAnnouncements] = useState([]);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (!user) navigate('/giris');
  }, [user, navigate]);

  // Status helper
  const getAnnouncementStatus = useCallback((item) => {
    const now = new Date();
    const endDate = new Date(item.endDate || item.auctionEndDate);
    if (endDate < now) return 'ended';
    if (item.isActive === false) return 'pending';
    return 'active';
  }, []);

  // Stats - client-side hesablama
  const stats = {
    total: allAnnouncements.length,
    active: allAnnouncements.filter(item => getAnnouncementStatus(item) === 'active').length,
    ended: allAnnouncements.filter(item => getAnnouncementStatus(item) === 'ended').length,
    pending: allAnnouncements.filter(item => getAnnouncementStatus(item) === 'pending').length
  };

  // Fetch all announcements
  const fetchAnnouncements = useCallback(async (pageNum = 1, reset = false) => {
    if (!user) return;

    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const response = await axios.get(
        `${API_URL}/ProductBase/User/${user.userId}`,
        { withCredentials: true }
      );

      const data = response.data;
      const items = data.Products || data.products || (Array.isArray(data) ? data : []);
      
      if (reset) {
        setAllAnnouncements(items);
      }

      // Filter
      let filtered = [...items];
      if (filterStatus !== 'all') {
        filtered = filtered.filter(item => getAnnouncementStatus(item) === filterStatus);
      }

      // Sort
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'oldest':
            return new Date(a.createdAt || a.createdDate) - new Date(b.createdAt || b.createdDate);
          case 'price-high':
            return (b.currentPrice || b.price || 0) - (a.currentPrice || a.price || 0);
          case 'price-low':
            return (a.currentPrice || a.price || 0) - (b.currentPrice || b.price || 0);
          case 'ending-soon':
            return new Date(a.endDate || a.auctionEndDate) - new Date(b.endDate || b.auctionEndDate);
          default:
            return new Date(b.createdAt || b.createdDate) - new Date(a.createdAt || a.createdDate);
        }
      });

      // Pagination
      const paged = filtered.slice(0, pageNum * PAGE_SIZE);
      setAnnouncements(paged);
      setHasMore(paged.length < filtered.length);
      setPage(pageNum);

    } catch (err) {
      console.error('Elanlar yüklənərkən xəta:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user, filterStatus, sortBy, getAnnouncementStatus]);

  useEffect(() => {
    if (user) fetchAnnouncements(1, true);
  }, [user]);

  useEffect(() => {
    if (user && allAnnouncements.length > 0) {
      let filtered = [...allAnnouncements];
      if (filterStatus !== 'all') {
        filtered = filtered.filter(item => getAnnouncementStatus(item) === filterStatus);
      }
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'oldest': return new Date(a.createdAt) - new Date(b.createdAt);
          case 'price-high': return (b.currentPrice || 0) - (a.currentPrice || 0);
          case 'price-low': return (a.currentPrice || 0) - (b.currentPrice || 0);
          default: return new Date(b.createdAt) - new Date(a.createdAt);
        }
      });
      setAnnouncements(filtered.slice(0, PAGE_SIZE));
      setHasMore(filtered.length > PAGE_SIZE);
      setPage(1);
    }
  }, [filterStatus, sortBy, allAnnouncements, getAnnouncementStatus]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading && allAnnouncements.length > 0) {
      let filtered = [...allAnnouncements];
      if (filterStatus !== 'all') {
        filtered = filtered.filter(item => getAnnouncementStatus(item) === filterStatus);
      }
      const nextPage = page + 1;
      const paged = filtered.slice(0, nextPage * PAGE_SIZE);
      setAnnouncements(paged);
      setHasMore(paged.length < filtered.length);
      setPage(nextPage);
    }
  }, [loadingMore, hasMore, loading, page, allAnnouncements, filterStatus, getAnnouncementStatus]);

  useInfiniteScroll(loadMore, 100, 50);

  const filteredAnnouncements = announcements.filter((item) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (item.name || item.title || '').toLowerCase().includes(search);
    }
    return true;
  });

  const handleRefresh = () => {
    setAllAnnouncements([]);
    fetchAnnouncements(1, true);
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="cabinet-announcements">
        <div className="cabinet-announcements__loading">
          <FaSpinner className="cabinet-announcements__spinner" />
          <p>Elanlar yüklənir...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cabinet-announcements">
      <div className="cabinet-announcements__header">
        <div className="cabinet-announcements__header-content">
          <h2 className="cabinet-announcements__title">Mənim Elanlarım</h2>
          <p className="cabinet-announcements__subtitle">Toplam {stats.total} elan</p>
        </div>
        <div className="cabinet-announcements__header-actions">
          <button onClick={handleRefresh} className="cabinet-announcements__refresh-btn" title="Yenilə"><FaRedo /></button>
          <button className="cabinet-announcements__add-btn" onClick={() => navigate('/yeni-herrac')}><FaPlus /><span>Yeni Elan</span></button>
        </div>
      </div>

      <div className="cabinet-announcements__stats">
        <div className="stat-card">
          <FaChartBar className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Toplam</span>
            <span className="stat-card__value">{stats.total}</span>
          </div>
        </div>
        <div className="stat-card stat-card--success">
          <FaCheckCircle className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Aktiv</span>
            <span className="stat-card__value">{stats.active}</span>
          </div>
        </div>
        <div className="stat-card stat-card--danger">
          <FaTimesCircle className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Bitmiş</span>
            <span className="stat-card__value">{stats.ended}</span>
          </div>
        </div>
        <div className="stat-card stat-card--warning">
          <FaClock className="stat-card__icon" />
          <div className="stat-card__content">
            <span className="stat-card__label">Gözləmədə</span>
            <span className="stat-card__value">{stats.pending}</span>
          </div>
        </div>
      </div>

      <div className="cabinet-announcements__toolbar">
        <div className="cabinet-announcements__search">
          <input type="text" placeholder="Elan adı ilə axtar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="cabinet-announcements__search-input" />
        </div>
        <div className="cabinet-announcements__controls">
          <button className={`cabinet-announcements__filter-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}><FaFilter /> Filtr</button>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="cabinet-announcements__sort-select">
            <option value="recent">📅 Ən Yeni</option>
            <option value="oldest">📅 Ən Qədim</option>
            <option value="price-high">💰 Yüksək Qiymət</option>
            <option value="price-low">💰 Aşağı Qiymət</option>
            <option value="ending-soon">⏰ Tezliklə Bitən</option>
          </select>
        </div>
      </div>

      {showFilters && (
        <div className="cabinet-announcements__filters">
          <div className="filter-group">
            <label>Status</label>
            <div className="filter-options">
              {[
                { key: 'all', label: '📋 Hamısı', count: stats.total },
                { key: 'active', label: '✅ Aktiv', count: stats.active },
                { key: 'ended', label: '🏁 Bitmiş', count: stats.ended }
              ].map(({ key, label, count }) => (
                <button key={key} className={`filter-option ${filterStatus === key ? 'active' : ''}`} onClick={() => setFilterStatus(key)}>{label} ({count})</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {filteredAnnouncements.length === 0 ? (
        <div className="cabinet-announcements__empty">
          <FaBox className="cabinet-announcements__empty-icon" />
          <h3>Elan tapılmadı</h3>
          <p>{allAnnouncements.length === 0 ? 'Hələ heç bir elan yerləşdirməmisiniz' : 'Bu filtrə uyğun elan yoxdur'}</p>
          <button className="cabinet-announcements__empty-btn" onClick={() => navigate('/yeni-herrac')}><FaPlus /> İlk Elanı Yarat</button>
        </div>
      ) : (
        <>
          <div className="cabinet-announcements__grid">
            {filteredAnnouncements.map((item) => {
              const status = getAnnouncementStatus(item);
              return (
                <div key={item.id} className={`cabinet-announcements__item ${status !== 'active' ? 'cabinet-announcements__item--inactive' : ''}`}>
                  <Announcement
                    id={item.id}
                    name={item.name || item.title}
                    title={item.name || item.title}
                    imageUrl={item.imageUrl}
                    images={item.images}
                    price={item.price || item.startingPrice}
                    currentPrice={item.currentPrice}
                    highestBid={item.highestBid}
                    endDate={item.endDate || item.auctionEndDate}
                    createdAt={item.createdAt || item.createdDate}
                    bidCount={item.bidCount}
                    city={item.city}
                    categoryName={item.categoryName}
                    subCategoryName={item.subCategoryName}
                    isActive={status === 'active'}
                    status={status}
                    showActions={true}
                    isOwner={true}
                  />
                  {status === 'ended' && (
                    <div className="cabinet-announcements__item-overlay">
                      <span className="cabinet-announcements__item-status">🏁 Bitmiş</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {loadingMore && (
            <div className="cabinet-announcements__load-more">
              <FaSpinner className="cabinet-announcements__spinner" />
              <span>Daha çox yüklənir...</span>
            </div>
          )}

          {!hasMore && announcements.length > 0 && (
            <div className="cabinet-announcements__end"><p>Bütün elanlar yükləndi</p></div>
          )}
        </>
      )}
    </div>
  );
}

export default CabinetAnnouncements;